import booleanContains from '@turf/boolean-contains'
import { useEffect, useMemo } from 'react'
import {
  aClient,
  aDrawnFeature,
  aMapCurrentCount,
  aMapViewState,
  aSelectedGeographyLayerFilter,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { useListingsQuery } from '~/query'
import { filterListingsByGeometry } from '~/lib/geography-layer-filter'
import { useClientFind } from './useClientFind'

export function useMapProperties() {
  const client = useAtomValue(aClient)
  const mapViewState = useAtomValue(aMapViewState)
  const setCurrentMapCount = useSetAtom(aMapCurrentCount)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const selectedGeographyLayer = useAtomValue(aSelectedGeographyLayerFilter)
  const propertiesFind = useClientFind(client!)

  const {
    listings: properties,
    count: totalCount,
    isLoading: isLoadingProperties,
    isFetching: isFetchingProperties,
    refetch,
  } = useListingsQuery({
    key: ['map-properties', 'disable-cache'],
    find: { ...propertiesFind },
    project: {
      slug: 1,
      name: 1,
      'property.address': 1,
      'property.address_2': 1,
      location: 1,
      type: 1,
      status: 1,
      property_types: 1,
    },
  })

  useEffect(() => {
    refetch()
  }, [propertiesFind])

  const drawnProperties = useMemo(() => {
    // Temporary, need to fix on the backend
    const filteredProperties = properties.filter((p) => p.location)
    const propertiesInDrawnArea = drawnFeature
      ? filteredProperties.filter((l: any) =>
          booleanContains(drawnFeature.geometry, l.location)
        )
      : filteredProperties

    return selectedGeographyLayer
      ? filterListingsByGeometry(
          propertiesInDrawnArea,
          selectedGeographyLayer.geometry
        )
      : propertiesInDrawnArea
  }, [properties, drawnFeature, selectedGeographyLayer])

  useEffect(() => {
    if (mapViewState) {
      setTimeout(() => {
        if (!isLoadingProperties)
          setCurrentMapCount(
            drawnProperties.filter((p) =>
              mapViewState!.bounds.contains(
                p.location.coordinates as [number, number]
              )
            ).length
          )
      }, 200)
    }
  }, [mapViewState, drawnProperties, isLoadingProperties, setCurrentMapCount])

  return {
    properties: drawnProperties,
    totalCount,
    isLoadingProperties,
    isFetchingProperties,
  }
}
